import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { GLAccount } from './glAccountServices';
import { GLTransaction } from './glTransactionServices';

// Define interfaces for Balance Sheet
export interface BalanceSheetItem {
  accountId: string;
  accountNumber: string;
  accountName: string;
  amount: number;
  level: number;
}

export interface BalanceSheetCategory {
  name: string;
  items: BalanceSheetItem[];
  total: number;
}

export interface BalanceSheetData {
  // Assets
  currentAssets: BalanceSheetCategory;
  nonCurrentAssets: BalanceSheetCategory;
  totalAssets: number;

  // Liabilities
  currentLiabilities: BalanceSheetCategory;
  nonCurrentLiabilities: BalanceSheetCategory;
  totalLiabilities: number;

  // Equity
  equity: BalanceSheetCategory;
  retainedEarnings: number; // Net Income from P&L
  totalEquity: number;

  // Balancing check
  totalLiabilitiesAndEquity: number;

  // Date
  asOfDate: Date;
  startDate?: Date;
}

/**
 * Generates a Balance Sheet as of a specific date
 * @param asOfDate The date to generate the balance sheet for (defaults to today)
 * @param startDate Optional start date for filtering transactions (for period-based calculations)
 * @returns Promise with BalanceSheetData
 */
export const generateBalanceSheet = async (
  asOfDate?: Date,
  startDate?: Date
): Promise<BalanceSheetData> => {
  try {
    const reportDate = asOfDate || new Date();

    // Step 1: Get all GL accounts
    const accountsSnapshot = await getDocs(collection(db, 'glAccounts'));
    const accounts: GLAccount[] = [];
    accountsSnapshot.forEach((doc) => {
      accounts.push({ id: doc.id, ...doc.data() } as GLAccount);
    });

    // Step 2: Get all transactions up to the as-of date
    let transactionsQuery;

    if (startDate) {
      // If start date is provided, get transactions between start and end date
      const nextDay = new Date(reportDate);
      nextDay.setDate(nextDay.getDate() + 1);

      transactionsQuery = query(
        collection(db, 'glTransactions'),
        where('transactionDate', '>=', Timestamp.fromDate(startDate)),
        where('transactionDate', '<', Timestamp.fromDate(nextDay))
      );
    } else {
      // Otherwise, get all transactions up to the as-of date
      const nextDay = new Date(reportDate);
      nextDay.setDate(nextDay.getDate() + 1);

      transactionsQuery = query(
        collection(db, 'glTransactions'),
        where('transactionDate', '<', Timestamp.fromDate(nextDay))
      );
    }

    const transactionsSnapshot = await getDocs(transactionsQuery);
    const transactions: GLTransaction[] = [];

    transactionsSnapshot.forEach((doc) => {
      const data = doc.data();
      const transaction = {
        id: doc.id,
        ...data,
        transactionDate: data.transactionDate instanceof Timestamp
          ? data.transactionDate.toDate()
          : data.transactionDate,
        entries: data.entries || []
      } as GLTransaction;

      transactions.push(transaction);
    });

    // Step 3: Calculate balances for each account
    const accountBalances: Record<string, number> = {};

    // Initialize all accounts with zero balances
    accounts.forEach(account => {
      if (account.id) {
        accountBalances[account.id] = 0;
      }
    });

    // Process all transaction entries
    transactions.forEach(transaction => {
      transaction.entries.forEach((entry) => {
        if (!entry.accountId) return;

        if (accountBalances[entry.accountId] === undefined) {
          accountBalances[entry.accountId] = 0;
        }

        // Find the account to determine its balance type
        const account = accounts.find(a => a.id === entry.accountId);
        if (!account) return;

        // Calculate balance based on account's normal balance type
        if (account.balanceType === 'debit') {
          // Debit-normal accounts: debits increase, credits decrease
          accountBalances[entry.accountId] += entry.debit - entry.credit;
        } else {
          // Credit-normal accounts: credits increase, debits decrease
          accountBalances[entry.accountId] += entry.credit - entry.debit;
        }
      });
    });

    // Step 4: Calculate Net Income from P&L accounts (Revenue - COGS - Expenses)
    let totalRevenue = 0;
    let totalCOGS = 0;
    let totalExpenses = 0;

    accounts.forEach(account => {
      if (!account.id) return;
      const balance = accountBalances[account.id];
      if (balance === 0) return;
      if (account.accountName.toLowerCase().includes('clearing')) return;

      const accountNumber = account.accountNumber;
      const firstDigit = accountNumber.charAt(0);

      // Calculate P&L components
      if (firstDigit === '6') { // Revenue
        totalRevenue += Math.abs(balance);
      } else if (firstDigit === '8') { // COGS
        totalCOGS += Math.abs(balance);
      } else if (firstDigit === '7') { // Expenses
        totalExpenses += Math.abs(balance);
      }
    });

    // Calculate Net Income (Retained Earnings for current period)
    const retainedEarnings = totalRevenue - totalCOGS - totalExpenses;

    // Step 5: Create Balance Sheet items by category
    const currentAssetItems: BalanceSheetItem[] = [];
    const nonCurrentAssetItems: BalanceSheetItem[] = [];
    const currentLiabilityItems: BalanceSheetItem[] = [];
    const nonCurrentLiabilityItems: BalanceSheetItem[] = [];
    const equityItems: BalanceSheetItem[] = [];

    accounts.forEach(account => {
      if (!account.id) return;

      const balance = accountBalances[account.id];

      // Skip accounts with zero balance and clearing accounts
      if (balance === 0) return;
      if (account.accountName.toLowerCase().includes('clearing')) return;

      // Only include detail accounts (level 3) for better detail
      // But include level 2 if it has a balance (for sub-totals)
      if (account.level !== 3 && account.level !== 2) return;

      const accountNumber = account.accountNumber;
      const firstDigit = accountNumber.charAt(0);

      const item: BalanceSheetItem = {
        accountId: account.id,
        accountNumber: account.accountNumber,
        accountName: account.accountName,
        amount: Math.abs(balance),
        level: account.level
      };

      // Categorize based on account number
      if (firstDigit === '1') { // Current Assets
        currentAssetItems.push(item);
      } else if (firstDigit === '2') { // Non-Current Assets
        nonCurrentAssetItems.push(item);
      } else if (firstDigit === '3') { // Current Liabilities
        currentLiabilityItems.push(item);
      } else if (firstDigit === '4') { // Non-Current Liabilities
        nonCurrentLiabilityItems.push(item);
      } else if (firstDigit === '5') { // Equity
        equityItems.push(item);
      }
      // Note: Revenue (6), Expenses (7), and COGS (8) are not balance sheet accounts
      // but are used to calculate Retained Earnings above
    });

    // Sort items by account number
    currentAssetItems.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
    nonCurrentAssetItems.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
    currentLiabilityItems.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
    nonCurrentLiabilityItems.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
    equityItems.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));

    // Calculate totals
    const totalCurrentAssets = currentAssetItems.reduce((sum, item) => sum + item.amount, 0);
    const totalNonCurrentAssets = nonCurrentAssetItems.reduce((sum, item) => sum + item.amount, 0);
    const totalCurrentLiabilities = currentLiabilityItems.reduce((sum, item) => sum + item.amount, 0);
    const totalNonCurrentLiabilities = nonCurrentLiabilityItems.reduce((sum, item) => sum + item.amount, 0);
    const totalEquityAccounts = equityItems.reduce((sum, item) => sum + item.amount, 0);

    // Total Equity = Equity Accounts + Retained Earnings (Net Income)
    const totalEquity = totalEquityAccounts + retainedEarnings;

    const totalAssets = totalCurrentAssets + totalNonCurrentAssets;
    const totalLiabilities = totalCurrentLiabilities + totalNonCurrentLiabilities;
    const totalLiabilitiesAndEquity = totalLiabilities + totalEquity;

    return {
      currentAssets: {
        name: 'Current Assets',
        items: currentAssetItems,
        total: totalCurrentAssets
      },
      nonCurrentAssets: {
        name: 'Non-Current Assets',
        items: nonCurrentAssetItems,
        total: totalNonCurrentAssets
      },
      totalAssets,
      currentLiabilities: {
        name: 'Current Liabilities',
        items: currentLiabilityItems,
        total: totalCurrentLiabilities
      },
      nonCurrentLiabilities: {
        name: 'Non-Current Liabilities',
        items: nonCurrentLiabilityItems,
        total: totalNonCurrentLiabilities
      },
      totalLiabilities,
      equity: {
        name: 'Equity',
        items: equityItems,
        total: totalEquityAccounts
      },
      retainedEarnings,
      totalEquity,
      totalLiabilitiesAndEquity,
      asOfDate: reportDate,
      startDate
    };
  } catch (error) {
    console.error('Error generating balance sheet:', error);
    throw error;
  }
};
